import { actionCreatorAsync } from "./actionHelpers";
import * as api from "../api/resourceCentre";
import { IThunkDispatch, RootState } from "../store";
import { ResourceCentre, Subscriptions } from "../interfaces/ResourceCentreInterface";
import { notificationAdd } from "./notification";
import {
  IFonepayDynamicQrFormData,
  INepalPayDynamicQrFormData,
  TQrPaymentInfo
} from "../interfaces/QrPayment";
import { AttachmentInterface } from "../interfaces/AttachmentInterface";

export enum Type {
  CHANGE_RESOURCE_CENTRE_PICTURE = "CHANGE_RESOURCE_CENTRE_PICTURE",
  UPDATE_USER_RC = "UPDATE_USER_RC"
}

export const uploadProfileResourceCentre =
  (rcObj: { formData: FormData; id: number }) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    await dispatch(
      actionCreatorAsync(Type.CHANGE_RESOURCE_CENTRE_PICTURE, async () =>
        api.uploadProfileResourceCentre(rcObj)
      )
    );
  };

export const getRCFromState = (state: RootState): ResourceCentre =>
  state.resources.resourceCentres.find((rc) => rc.id === state.userContext.resourceCentreId) ||
  state.userContext.resourceCentre;

export const getSubscriptions = (
  subscriptions: Subscriptions["features"]
): {
  billing: boolean;
  stock: boolean;
  campaign: boolean;
  ipd: boolean;
  medical: boolean;
  hmis: boolean;
} => ({
  billing: subscriptions?.billing?.subscribed,
  stock: subscriptions?.stock?.subscribed,
  campaign: subscriptions?.campaign?.subscribed,
  ipd: subscriptions?.ipd?.subscribed,
  medical: subscriptions?.ipd?.subscribed,
  hmis: subscriptions?.hmis?.subscribed
});

export const updateUserResourceCentre =
  (resourceCentre: Partial<ResourceCentre>) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    dispatch({
      type: Type.UPDATE_USER_RC,
      payload: resourceCentre
    });
  };

export const updateQrData =
  (resourceCentreId: number, qrData: TQrPaymentInfo) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    try {
      const updatedRc: ResourceCentre = await api.patchQrPaymentInfo(resourceCentreId, qrData);
      dispatch({
        type: Type.UPDATE_USER_RC,
        payload: updatedRc
      });
    } catch (error: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: error.message,
          autoTimeout: true
        })
      );
    }
  };

export const updateNepalPayDynamicQrData =
  (resourceCentreId: number, dynamicQrData: Partial<INepalPayDynamicQrFormData>) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    try {
      const updatedRc: ResourceCentre = await api.patchNepalPayDynamicQrPaymentInfo(
        resourceCentreId,
        dynamicQrData
      );
      dispatch({
        type: Type.UPDATE_USER_RC,
        payload: updatedRc
      });
    } catch (error: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: error.message,
          autoTimeout: true
        })
      );
    }
  };

export const updateFonepayDynamicQrData =
  (resourceCentreId: number, data: Partial<IFonepayDynamicQrFormData>) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    try {
      const updatedRc: ResourceCentre = await api.patchFonepayDynamicQrPaymentInfo(
        resourceCentreId,
        data
      );
      dispatch({
        type: Type.UPDATE_USER_RC,
        payload: updatedRc
      });
    } catch (error: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: error.message,
          autoTimeout: true
        })
      );
    }
  };

export const deleteQrImage =
  (rcId: number, paymentMethod: string, qrImageKey: string) =>
  async (dispatch: IThunkDispatch): Promise<void> => {
    try {
      const updatedRc = await api.deleteQrPaymentImage(rcId, paymentMethod, qrImageKey);
      dispatch({
        type: Type.UPDATE_USER_RC,
        payload: updatedRc
      });
    } catch (err: any) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message: err.message || "Failed to delete qr image",
          autoTimeout: true
        })
      );
    }
  };

// Patch assessmentTemplateImages in the resources.resourceCentres state after upload
export const addAssessmentTemplateImageToRC =
  (newImage: AttachmentInterface) =>
  (dispatch: IThunkDispatch, getState: () => RootState): void => {
    const state = getState();

    const rcId = state.userContext?.resourceCentreId;
    const currentRc = state.resources?.resourceCentres?.find(
      (rc: ResourceCentre) => rc.id === rcId
    );

    if (!currentRc) return;
    const updatedRc: ResourceCentre = {
      ...currentRc,
      assessmentTemplateImages: [...(currentRc.assessmentTemplateImages || []), newImage]
    };

    dispatch({ type: "GET_RESOURCECENTRE", payload: updatedRc });
    dispatch({ type: "UPDATE_USER_RC", payload: updatedRc });
  };

// Patch assessmentTemplateImages in the resources.resourceCentres state after delete
export const removeAssessmentTemplateImageFromRC =
  (imageId: number) =>
  (dispatch: IThunkDispatch, getState: () => RootState): void => {
    const state = getState();
    const rcId = state.userContext?.resourceCentreId;
    const currentRc = state.resources?.resourceCentres?.find(
      (rc: ResourceCentre) => rc.id === rcId
    );
    if (!currentRc) return;
    const updatedRc: ResourceCentre = {
      ...currentRc,
      assessmentTemplateImages: (currentRc.assessmentTemplateImages || []).filter(
        (img: AttachmentInterface) => img.id !== imageId
      )
    };
    dispatch({ type: "GET_RESOURCECENTRE", payload: updatedRc });
    dispatch({ type: "UPDATE_USER_RC", payload: updatedRc });
  };

// Set in bulk, the assessmentTemplateImages, when loading them from the API initially
export const setAssessmentTemplateImagesInRC =
  (images: AttachmentInterface[]) =>
  (dispatch: IThunkDispatch, getState: () => RootState): void => {
    const state = getState();

    const rcId = state.userContext?.resourceCentreId;
    const currentRc = state.resources?.resourceCentres?.find(
      (rc: ResourceCentre) => rc.id === rcId
    );

    if (!currentRc) return;

    const updatedRc: ResourceCentre = {
      ...currentRc,
      assessmentTemplateImages: images
    };

    dispatch({ type: "GET_RESOURCECENTRE", payload: updatedRc });
    dispatch({ type: "UPDATE_USER_RC", payload: updatedRc });
  };
